import { Component } from '@theme/component';
import { prepareComponents } from '@theme/component-loader';
import { sectionRenderer } from '@theme/section-renderer';
import { viewTransition } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
import { getPreloadMargin, scheduleBackgroundTask } from '@theme/loading-scheduler';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;
  /** @type {IntersectionObserver | undefined} */
  #prefetchObserver;
  /** @type {IntersectionObserver | undefined} */
  #cardObserver;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {MutationObserver | undefined} */
  #gridObserver;
  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;
  /** @type {Map<string, { controller: AbortController, promise: Promise<string>, promote: () => void }>} */
  #pendingPages = new Map();
  /** @type {Set<unknown>} */
  #reportedErrors = new Set();
  /** @type {Set<string>} */
  #rendering = new Set();
  /** @type {Set<HTMLElement>} */
  #observedCards = new Set();
  #generation = 0;
  #filterPending = false;
  #layoutFrame = 0;
  #gridWidth = 0;

  connectedCallback() {
    super.connectedCallback();
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard instanceof HTMLElement) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    }
    this.#observeViewMore();
    this.#gridObserver = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type !== 'attributes' || !(record.target instanceof Element)) continue;
        if (record.attributeName === 'open' || record.attributeName === 'aria-expanded') {
          const card = record.target.closest('[ref="cards[]"]');
          if (card instanceof HTMLElement && card.querySelector('[open], [aria-expanded="true"]')) {
            card.style.contentVisibility = 'visible';
          }
        }
      }
      if (records.some((record) => record.type === 'attributes' && record.attributeName === 'product-grid-view')) {
        this.#resetCardVisibility();
      }
      this.#scheduleLayout();
    });
    this.#gridObserver.observe(this, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['product-grid-view', 'data-last-page', 'open', 'aria-expanded'],
    });
    this.#resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined && width !== this.#gridWidth) {
        this.#gridWidth = width;
        this.#resetCardVisibility();
        this.#scheduleLayout();
      }
    });
    if (this.refs.grid) this.#resizeObserver.observe(this.refs.grid);
    this.addEventListener('focusin', this.#revealFocusedCard);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    this.#scheduleLayout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#invalidatePages();
    this.infinityScrollObserver?.disconnect();
    this.#prefetchObserver?.disconnect();
    this.#gridObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#resetCardVisibility();
    cancelAnimationFrame(this.#layoutFrame);
    this.removeEventListener('focusin', this.#revealFocusedCard);
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    if (this.#filterPending || !this.isConnected) return;
    const { viewMorePrevious, viewMoreNext } = this.refs;
    this.infinityScrollObserver ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (entry.target === this.refs.viewMorePrevious) void this.#renderPage('previous');
          if (entry.target === this.refs.viewMoreNext) void this.#renderPage('next');
        }
      }, { rootMargin: '100px' }
    );
    this.#prefetchObserver ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.target !== this.refs.viewMoreNext) continue;
          const page = this.#getPage('next');
          if (this.#shouldUsePage(page) && page) {
            const generation = this.#generation;
            void this.#fetchPage(page).catch((error) => {
              if (this.isConnected && generation === this.#generation && error?.name !== 'AbortError') {
                this.#reportError(error);
              }
            });
          }
        }
      }, { rootMargin: `${getPreloadMargin()}px` }
    );
    this.infinityScrollObserver.disconnect();
    this.#prefetchObserver.disconnect();
    for (const sentinel of [viewMorePrevious, viewMoreNext]) {
      if (!sentinel) continue;
      this.infinityScrollObserver.observe(sentinel);
      if (sentinel === viewMoreNext) this.#prefetchObserver.observe(sentinel);
    }
  }

  /** @param {{ page: number, url: URL } | undefined} page */
  #shouldUsePage(page) {
    const lastPage = Number(this.refs.grid?.dataset.lastPage);
    return !!page && page.page >= 1 && page.page <= lastPage;
  }

  /** @param {{ page: number, url: URL }} page @param {boolean} [foreground] @returns {Promise<string>} */
  #fetchPage(page, foreground = false) {
    if (this.#filterPending || !this.isConnected) {
      return Promise.reject(new DOMException('Pagination suspended during filtering', 'AbortError'));
    }
    const key = page.url.href;
    const pending = this.#pendingPages.get(key);
    if (pending) {
      if (foreground) pending.promote();
      return pending.promise;
    }
    const controller = new AbortController();
    const scheduled = scheduleBackgroundTask(
      () => sectionRenderer.getSectionHTML(this.sectionId, true, page.url, controller.signal),
      { signal: controller.signal }
    );
    if (foreground) scheduled.promote();
    const promise = scheduled.promise.finally(() => {
      if (this.#pendingPages.get(key)?.controller === controller) this.#pendingPages.delete(key);
    });
    this.#pendingPages.set(key, { controller, promise, promote: scheduled.promote });
    return promise;
  }

  /** @param {'previous' | 'next'} type */
  async #renderPage(type) {
    if (this.#filterPending || !this.isConnected || this.#rendering.has(type)) return;
    this.#rendering.add(type);
    const generation = this.#generation;
    try {
      if (viewTransition.current) await viewTransition.current;
      const { grid } = this.refs;
      const page = this.#getPage(type);
      if (!grid || !page || !this.#shouldUsePage(page) || generation !== this.#generation) return;
      const html = await this.#fetchPage(page, true);
      if (!this.isConnected || generation !== this.#generation) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incomingGrid = parsed.querySelector('[ref="grid"]');
      const cards = incomingGrid?.querySelectorAll(':scope > [ref="cards[]"]');
      if (!incomingGrid || !cards?.length) throw new Error('Missing products in pagination response');
      await prepareComponents(incomingGrid);
      if (!this.isConnected || generation !== this.#generation || grid !== this.refs.grid) return;
      const currentPage = this.#getPage(type);
      if (currentPage?.url.href !== page.url.href) return;

      const currentScrollTop = getScrollTop();
      const firstElement = grid.firstElementChild;
      const oldTop = firstElement?.getBoundingClientRect().top ?? 0;
      if (type === 'previous') grid.prepend(...cards);
      else grid.append(...cards);
      this.#aspectRatioHelper?.processNewElements();
      if (type === 'previous' && firstElement) {
        scrollTo({ top: currentScrollTop + firstElement.getBoundingClientRect().top - oldTop, behavior: 'instant' });
      }
      history.pushState('', '', page.url.toString());
      this.#scheduleLayout();
    } catch (error) {
      if (generation === this.#generation) this.#reportError(error);
    } finally {
      this.#rendering.delete(type);
      if (this.isConnected && generation !== this.#generation) this.#scheduleLayout();
    }
  }

  /** @param {'previous' | 'next'} type @returns {{ page: number, url: URL } | undefined} */
  #getPage(type) {
    const cards = this.refs.grid?.querySelectorAll(':scope > [ref="cards[]"]');
    if (!cards?.length) return;
    const target = cards[type === 'previous' ? 0 : cards.length - 1];
    if (!(target instanceof HTMLElement)) return;
    const page = Number(target.dataset.page) + (type === 'previous' ? -1 : 1);
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return { page, url };
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #invalidatePages() {
    this.#generation += 1;
    this.#reportedErrors.clear();
    this.#filterPending = false;
    for (const { controller } of this.#pendingPages.values()) controller.abort();
    this.#pendingPages.clear();
  }

  /** @param {Event} event */
  #handleFilterUpdate = (event) => {
    const section = event.target instanceof Element ? event.target.closest('[id^="shopify-section-"]') : null;
    if (section && section.id !== `shopify-section-${this.sectionId}`) return;
    this.#invalidatePages();
    this.infinityScrollObserver?.disconnect();
    this.#prefetchObserver?.disconnect();
    this.#resetCardVisibility();
    const generation = this.#generation;
    if ('promise' in event && event.promise instanceof Promise) {
      this.#filterPending = true;
      const resume = () => {
        if (!this.isConnected || generation !== this.#generation) return;
        this.#filterPending = false;
        this.#scheduleLayout();
      };
      event.promise.then(resume, resume);
    }
    // While a filter promise is pending, mutations in facet summaries must not
    // restart pagination against the previous grid and the new filter URL.
  };

  #scheduleLayout() {
    cancelAnimationFrame(this.#layoutFrame);
    if (this.#filterPending || !this.isConnected) return;
    this.#layoutFrame = requestAnimationFrame(() => {
      if (this.#filterPending || !this.isConnected) return;
      this.#observeViewMore();
      this.#updateCardVisibility();
    });
  }

  #resetCardVisibility() {
    this.#cardObserver?.disconnect();
    for (const card of this.#observedCards) {
      card.style.removeProperty('content-visibility');
      card.style.removeProperty('contain-intrinsic-size');
    }
    this.#observedCards.clear();
  }

  #updateCardVisibility() {
    const cards = this.refs.grid?.querySelectorAll(':scope > [ref="cards[]"]');
    if (!cards || cards.length <= 100 || !CSS.supports('content-visibility', 'auto')) {
      this.#resetCardVisibility();
      return;
    }
    this.#cardObserver ??= new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const card = entry.target;
        if (!(card instanceof HTMLElement)) continue;
        // Visible/interactive cards have no paint containment, so popovers can overflow.
        const interactive = card.matches(':focus-within') || card.querySelector('[open], [aria-expanded="true"]');
        card.style.contentVisibility = entry.isIntersecting || interactive ? 'visible' : 'auto';
      }
    }, { rootMargin: `${getPreloadMargin()}px` });
    const measurements = Array.from(cards).filter(
      (card) => card instanceof HTMLElement && !this.#observedCards.has(card)
    ).map((card) => ({ card, height: card.getBoundingClientRect().height }));
    for (const { card, height } of measurements) {
      if (!(card instanceof HTMLElement)) continue;
      card.style.containIntrinsicSize = `auto none auto ${Math.ceil(height)}px`;
      this.#observedCards.add(card);
      this.#cardObserver.observe(card);
    }
  }

  /** @param {unknown} error */
  #reportError(error) {
    if (this.#reportedErrors.has(error)) return;
    this.#reportedErrors.add(error);
    this.dispatchEvent(new CustomEvent('pagination:error', { bubbles: true, detail: { error } }));
  }

  /** @param {FocusEvent} event */
  #revealFocusedCard = (event) => {
    const card = event.target instanceof Element ? event.target.closest('[ref="cards[]"]') : null;
    if (card instanceof HTMLElement) card.style.contentVisibility = 'visible';
  };
}
